import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { backendUrl } from '../utils/variables';
import { MyContext } from '../MainContext';
import { useCustomAlert } from './CustomAlert';
import './PublicGroupView.css';

/** get_public_group_details returns group_id; get_group_by_public_url returns id */
const resolveGroupId = (g) => g?.group_id ?? g?.id ?? null;

const PublicGroupView = () => {
    const { groupIdentifier } = useParams();
    const navigate = useNavigate();
    const { user } = useContext(MyContext);
    const { showAlert, AlertComponent } = useCustomAlert();
    const [group, setGroup] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isMember, setIsMember] = useState(false);
    const [hasPendingRequest, setHasPendingRequest] = useState(false);
    const [hasRejectedRequest, setHasRejectedRequest] = useState(false);
    const [joinBlocked, setJoinBlocked] = useState(false);
    const [rejectionReason, setRejectionReason] = useState('');
    const [sendingRequest, setSendingRequest] = useState(false);
    const [showDirectionsModal, setShowDirectionsModal] = useState(false);

    useEffect(() => {
        // Fetch group details
        fetchGroupDetails();
    }, [groupIdentifier, user]);

    // Periodically check membership status if user is logged in but not a member
    useEffect(() => {
        if (user && user.user_id && group && !isMember && !hasPendingRequest && !hasRejectedRequest) {
            const interval = setInterval(() => {
                refreshMembershipStatus();
            }, 10000); // Check every 10 seconds

            return () => clearInterval(interval);
        }
    }, [user, group, isMember, hasPendingRequest, hasRejectedRequest]);

    const fetchGroupDetails = async () => {
        try {
            let groupData;
            
            // First, get the group details to find the group ID
            if (/^\d+$/.test(groupIdentifier)) {
                // It's a numeric group ID
                const response = await fetch(`${backendUrl}/get_public_group_details.php`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        group_id: parseInt(groupIdentifier),
                        user_id: user?.user_id || 0
                    })
                });
                
                const data = await response.json();
                if (data.success) {
                    groupData = data.data;
                    setGroup(groupData);
                    
                    // If user is logged in and is a member, redirect immediately
                    if (user && user.user_id && groupData.is_member) {
                        navigate(`/group/${resolveGroupId(groupData)}/detail`, { replace: true });
                        return;
                    }
                    
                    // Set membership status
                    if (user && user.user_id) {
                        setIsMember(groupData.is_member);
                        setHasPendingRequest(groupData.has_pending_request);
                        setHasRejectedRequest(groupData.has_rejected_request);
                        setJoinBlocked(!!groupData.join_blocked);
                        setRejectionReason(groupData.rejection_reason || '');
                    }
                } else {
                    setError(data.error || 'Group not found');
                    return;
                }
            } else {
                // It's a public URL - get group info first
                const groupResponse = await fetch(`${backendUrl}/get_group_by_public_url.php?public_url=${groupIdentifier}`);
                const groupResult = await groupResponse.json();
                
                if (!groupResult.success) {
                    setError(groupResult.error || 'Group not found');
                    return;
                }
                
                groupData = groupResult.group;
                setGroup(groupData);
                
                // For public URLs, we need to check membership separately
                if (user && user.user_id) {
                    const isMemberResult = await checkMembershipForPublicUrl(resolveGroupId(groupData));
                    if (isMemberResult) {
                        return; // Already redirected
                    }
                }
            }
        } catch (err) {
            setError('Failed to load group details');
        } finally {
            setLoading(false);
        }
    };

    const checkMembershipForPublicUrl = async (groupId) => {
        try {
            const response = await fetch(`${backendUrl}/get_public_group_details.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    group_id: groupId,
                    user_id: user.user_id
                })
            });

            const data = await response.json();
            if (data.success && data.data) {
                const membershipData = data.data;
                setIsMember(membershipData.is_member);
                setHasPendingRequest(membershipData.has_pending_request);
                setHasRejectedRequest(membershipData.has_rejected_request);
                setJoinBlocked(!!membershipData.join_blocked);
                setRejectionReason(membershipData.rejection_reason || '');
                
                // If user is a member, redirect immediately to group detail page
                if (membershipData.is_member) {
                    navigate(`/group/${resolveGroupId(membershipData)}/detail`, { replace: true });
                    return true; // Indicates redirect happened
                }
            }
            return false; // No redirect needed
        } catch (err) {
            console.error('Error checking membership:', err);
            return false;
        }
    };

    const refreshMembershipStatus = async () => {
        const gid = resolveGroupId(group);
        if (gid && user && user.user_id) {
            const redirected = await checkMembershipForPublicUrl(gid);
            if (redirected) {
                // User was redirected, no need to continue
                return;
            }
        }
    };

    const handleJoinGroup = () => {
        if (!user || !user.user_id) {
            // Redirect to login/register page
            navigate('/', { 
                state: { 
                    redirectTo: `/group/${groupIdentifier}`,
                    message: 'Please create an account or login to join this group'
                }
            });
        } else {
            // Create join request
            createJoinRequest();
        }
    };

    const createJoinRequest = async () => {
        if (sendingRequest) return;

        const groupId = resolveGroupId(group);
        if (!groupId) {
            showAlert('Group information is incomplete. Please refresh and try again.', 'error');
            return;
        }
        
        setSendingRequest(true);
        try {
            const response = await fetch(`${backendUrl}/create_group_join_request.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    group_id: groupId,
                    user_id: user.user_id
                })
            });

            const data = await response.json();
            
            if (data.success) {
                setHasPendingRequest(true);
                setHasRejectedRequest(false);
                showAlert('Join request sent successfully! The group admin will review your request.', 'success');
            } else {
                if (data.join_blocked || (data.error && data.error.toLowerCase().includes('not allowed to join'))) {
                    setJoinBlocked(true);
                    setHasRejectedRequest(false);
                    showAlert(data.error || `You are not allowed to join ${group?.group_name || group?.name || 'this group'}`, 'error');
                } else if (data.error && data.error.includes('already a member')) {
                    setIsMember(true);
                    navigate(`/group/${groupId}/detail`, { replace: true });
                } else {
                    showAlert(data.error || 'Failed to send join request', 'error');
                }
            }
        } catch (err) {
            showAlert('Failed to send join request', 'error');
        } finally {
            setSendingRequest(false);
        }
    };

    // Helper function to safely parse dates for Safari compatibility
    const parseDate = (dateString) => {
        if (!dateString) return null;
        
        // If it's already a Date object, return it
        if (dateString instanceof Date) return dateString;
        
        // Convert "YYYY-MM-DD HH:MM:SS" to "YYYY-MM-DDTHH:MM:SS" for Safari compatibility
        const isoDateString = dateString.replace(' ', 'T');
        
        // Try parsing with ISO format first (Safari compatible)
        try {
            const date = new Date(isoDateString);
            if (!isNaN(date.getTime())) {
                return date;
            }
        } catch (error) {
            console.error('Error parsing ISO date:', isoDateString, error);
        }
        
        // Fallback to manual parsing for "YYYY-MM-DD HH:MM:SS" format
        const match = dateString.match(/^(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2}):(\d{2})$/);
        if (match) {
            const [, year, month, day, hour, minute, second] = match;
            // Create date as local time (not UTC)
            const date = new Date(
                parseInt(year, 10),
                parseInt(month, 10) - 1, // Month is 0-indexed
                parseInt(day, 10),
                parseInt(hour, 10),
                parseInt(minute, 10),
                parseInt(second, 10)
            );
            
            return isNaN(date.getTime()) ? null : date;
        }
        
        // Final fallback to regular Date parsing
        try {
            const date = new Date(dateString);
            return isNaN(date.getTime()) ? null : date;
        } catch (error) {
            console.error('Error parsing date:', dateString, error);
            return null;
        }
    };

    const openDirectionsFromMyLocation = () => {
        const lat = group?.latitude != null ? Number(group.latitude) : null;
        const lng = group?.longitude != null ? Number(group.longitude) : null;
        if (lat == null || lng == null) return;
        if (!navigator.geolocation) {
            window.open(`https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`, '_blank');
            setShowDirectionsModal(false);
            return;
        }
        navigator.geolocation.getCurrentPosition(
            (pos) => {
                const o = `${pos.coords.latitude},${pos.coords.longitude}`;
                const d = `${lat},${lng}`;
                window.open(`https://www.google.com/maps/dir/?api=1&origin=${o}&destination=${d}`, '_blank');
                setShowDirectionsModal(false);
            },
            () => {
                window.open(`https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`, '_blank');
                setShowDirectionsModal(false);
            }
        );
    };

    const displayName = group?.name || group?.group_name || 'Group';
    const plainDescription = String(group?.description || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
    const addressText = String(group?.address || '').trim();
    const hasLocation = group?.latitude != null && group?.longitude != null
        && !Number.isNaN(Number(group.latitude)) && !Number.isNaN(Number(group.longitude));
    const showChurchMap = String(group?.group_type || '').toLowerCase() === 'church' && hasLocation;

    const formatDate = (dateString) => {
        const date = parseDate(dateString);
        if (!date) return 'Invalid date';
        
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    if (loading) {
        return (
            <div className="public-group-loading">
                <div className="loading-spinner"></div>
                <p>Loading group details...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="public-group-error">
                <h2>Group Not Found</h2>
                <p>{error}</p>
                <button onClick={() => navigate('/')} className="btn-primary">
                    Go Home
                </button>
            </div>
        );
    }

    return (
        <div className="public-group-container">
            <AlertComponent />
            <div className="public-group-header">
                <div className="group-info">
                    {group.group_photo && (
                        <img 
                            src={group.group_photo} 
                            alt={displayName}
                            className="group-photo"
                        />
                    )}
                    <div className="group-details">
                        <h1 className="group-name">{displayName}</h1>
                        <div className="group-meta">
                            <span className="group-type sr-only">{group.group_type}</span>
                            <span className="member-count sr-only">{group.member_count} members</span>
                            <span className="created-date">Created {formatDate(group.created_at)}</span>
                        </div>
                    </div>
                </div>
            </div>

            <div className="public-group-content">
                {(plainDescription || addressText || showChurchMap) && (
                <div className="church-detail-block">
                {plainDescription ? (
                    <p className="church-detail-description">{plainDescription}</p>
                ) : null}
                {addressText && !showChurchMap ? (
                    <div className="church-detail-address">
                        <span className="church-detail-pin">📍</span>
                        <span>{addressText}</span>
                    </div>
                ) : null}
                {showChurchMap && (
                    <div className="church-detail-location">
                        {addressText ? (
                            <div className="church-detail-address church-detail-address-map">
                                <span className="church-detail-pin church-detail-pin-map">📍</span>
                                <span>{addressText}</span>
                            </div>
                        ) : null}
                        <div className="church-detail-map">
                            <iframe
                                title="Church location"
                                src={`https://www.google.com/maps?q=${group.latitude},${group.longitude}&z=15&output=embed`}
                                width="100%"
                                height="180"
                                style={{ border: 0, display: 'block' }}
                                allowFullScreen
                                loading="lazy"
                                referrerPolicy="no-referrer-when-downgrade"
                            />
                        </div>
                        <button type="button" className="church-detail-directions" onClick={() => setShowDirectionsModal(true)}>
                            Get directions
                        </button>
                    </div>
                )}
                </div>
                )}

                <div className="group-admins sr-only">
                    <h3>Group Administrators</h3>
                    <div className="admins-list">
                        {(group.admins || []).map(admin => (
                            <div key={admin.id} className="admin-card">
                                {admin.profile_picture && (
                                    <img 
                                        src={admin.profile_picture} 
                                        alt={admin.username}
                                        className="admin-photo"
                                    />
                                )}
                                <div className="admin-info">
                                    <h4>{admin.first_name} {admin.last_name}</h4>
                                    <p>@{admin.username}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="join-section">
                    <h3>Join This Group</h3>
                    <p>
                        {!user || !user.user_id
                            ? 'Create an account to begin - usually takes less than 5 minutes!'
                            : joinBlocked
                                ? `You are not allowed to join ${group?.group_name || group?.name || 'this group'}.`
                                : hasPendingRequest
                                    ? 'Your join request is pending approval from group administrators.'
                                    : hasRejectedRequest
                                        ? `Your join request was rejected.${rejectionReason ? ` Reason: ${rejectionReason}` : ''} You can request again.`
                                        : isMember
                                            ? 'You are already a member of this group.'
                                            : `Welcome ${user?.first_name || user?.username}! Click below to request to join this group.`
                        }
                    </p>
                    {!hasPendingRequest && !joinBlocked && !isMember && (
                    <button 
                        onClick={handleJoinGroup}
                        className="join-group-btn"
                            disabled={sendingRequest}
                        >
                            {sendingRequest 
                                ? 'Sending Request...' 
                                : user && user.user_id 
                                    ? (hasRejectedRequest ? 'Request Again' : 'Request to Join Group')
                                    : 'Create Account to Join'
                            }
                    </button>
                    )}
                    {hasPendingRequest && (
                        <div className="pending-request">
                            <span className="pending-badge">Request Pending</span>
                        </div>
                    )}
                    {joinBlocked && (
                        <div className="rejected-request">
                            <span className="rejected-badge">Not allowed to join</span>
                        </div>
                    )}
                    {hasRejectedRequest && !joinBlocked && (
                        <div className="rejected-request">
                            <span className="rejected-badge">Request Rejected</span>
                        </div>
                    )}
                </div>

                <div className="group-features">
                    <h3>What You'll Get</h3>
                    <div className="features-grid">
                        <div className="feature-item sr-only">
                            <div className="feature-icon">💬</div>
                            <h4>Group Chat</h4>
                            <p>Connect and chat with other group members</p>
                        </div>
                        <div className="feature-item">
                            <div className="feature-icon">📢</div>
                            <h4>Announcements</h4>
                            <p>Stay updated with group announcements</p>
                        </div>

                        <div className="feature-item">
                            <div className="feature-icon">🎯</div>
                            <h4>Group Goals</h4>
                            <p>Set and track goals together</p>
                        </div>

                        <div className="feature-item">
                            <div className="feature-icon">🙏</div>
                            <h4>Prayer Requests</h4>
                            <p>Share and support prayer requests</p>
                        </div>
                      
                        
                    </div>
                </div>
            </div>

            {showDirectionsModal && (
                <div className="public-group-modal-overlay" onClick={() => setShowDirectionsModal(false)} role="presentation">
                    <div className="public-group-modal" onClick={(e) => e.stopPropagation()} role="dialog">
                        <h3 className="public-group-modal-title">Get directions</h3>
                        <p className="public-group-modal-subtitle">Open in Google Maps with your current location as start.</p>
                        <button type="button" className="join-group-btn" style={{ marginBottom: 12 }} onClick={openDirectionsFromMyLocation}>
                            Open in Google Maps
                        </button>
                        <button type="button" className="public-group-modal-cancel" onClick={() => setShowDirectionsModal(false)}>
                            Cancel
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PublicGroupView;
